import { useEffect, useMemo, useState } from 'react'
import {
  averageProfitMarginForProjectsStartedSince,
  buildLargeProjectMarginRows,
  buildMarginByClientTypeFromProjects,
  buildTransactionalProjectMarginRows,
  PROJECT_LABOUR_RATE_PER_HOUR,
  type AverageProfitMarginResult,
  type ProjectMarginRow,
  type MarginByClientTypeRow,
} from '../components/ProfitabilityPerformance/mockData'
import { EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE } from '../data/executiveSummaryData'
import { fetchProjectProfitabilityFromFirestore } from '../services/pwfProfitabilitySnapshotService'

function roundMarginPct(marginPct: number | null): number | null {
  return marginPct != null ? Math.round(marginPct * 10) / 10 : null
}

export function useMarginEfficiencyMargins() {
  const [recordedAt, setRecordedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [transactional, setTransactional] = useState<AverageProfitMarginResult>({
    marginPct: null,
    projectCount: 0,
    invoicedTotal: 0,
  })
  const [largeProject, setLargeProject] = useState<AverageProfitMarginResult>({
    marginPct: null,
    projectCount: 0,
    invoicedTotal: 0,
  })
  const [marginByClientType, setMarginByClientType] = useState<MarginByClientTypeRow[]>(() =>
    buildMarginByClientTypeFromProjects([], EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE)
  )
  const [largeProjectRows, setLargeProjectRows] = useState<ProjectMarginRow[]>([])
  const [transactionalProjectRows, setTransactionalProjectRows] = useState<ProjectMarginRow[]>([])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const snapshot = await fetchProjectProfitabilityFromFirestore()
        if (cancelled) return
        if (!snapshot) {
          setRecordedAt(null)
          setTransactional({ marginPct: null, projectCount: 0, invoicedTotal: 0 })
          setLargeProject({ marginPct: null, projectCount: 0, invoicedTotal: 0 })
          setMarginByClientType(
            buildMarginByClientTypeFromProjects([], EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE)
          )
          setLargeProjectRows([])
          setTransactionalProjectRows([])
          return
        }
        const { projectProfitability } = snapshot
        const startedFrom = EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE
        setRecordedAt(snapshot.recordedAt)
        setTransactional(
          averageProfitMarginForProjectsStartedSince(
            projectProfitability,
            startedFrom,
            PROJECT_LABOUR_RATE_PER_HOUR,
            'Transactional'
          )
        )
        setLargeProject(
          averageProfitMarginForProjectsStartedSince(
            projectProfitability,
            startedFrom,
            PROJECT_LABOUR_RATE_PER_HOUR,
            'Large Project'
          )
        )
        setMarginByClientType(
          buildMarginByClientTypeFromProjects(
            projectProfitability,
            startedFrom,
            PROJECT_LABOUR_RATE_PER_HOUR
          )
        )
        setLargeProjectRows(
          buildLargeProjectMarginRows(projectProfitability, startedFrom, PROJECT_LABOUR_RATE_PER_HOUR)
        )
        setTransactionalProjectRows(
          buildTransactionalProjectMarginRows(projectProfitability, startedFrom, PROJECT_LABOUR_RATE_PER_HOUR)
        )
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load margin data')
          setTransactional({ marginPct: null, projectCount: 0, invoicedTotal: 0 })
          setLargeProject({ marginPct: null, projectCount: 0, invoicedTotal: 0 })
          setMarginByClientType(
            buildMarginByClientTypeFromProjects([], EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE)
          )
          setLargeProjectRows([])
          setTransactionalProjectRows([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return {
    transactionalMarginPct: useMemo(() => roundMarginPct(transactional.marginPct), [transactional.marginPct]),
    transactionalProjectCount: transactional.projectCount,
    largeProjectMarginPct: useMemo(() => roundMarginPct(largeProject.marginPct), [largeProject.marginPct]),
    largeProjectCount: largeProject.projectCount,
    marginByClientType: useMemo(
      () =>
        marginByClientType.map((row) => ({
          ...row,
          marginPct: roundMarginPct(row.marginPct),
        })),
      [marginByClientType]
    ),
    largeProjectRows,
    transactionalProjectRows,
    startDateFrom: EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE,
    recordedAt,
    loading,
    error,
  }
}
